import { DecodedToken } from "@cohesive/api-client";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

export function getOrgIdFromToken(token?: string): string {
  const decodedToken = jwtDecode(token as string) as DecodedToken;

  if (decodedToken.c_org_id) {
    return decodedToken.c_org_id;
  }

  throw new Error("Could not find organization ID in token");
}

export function getUserIdFromToken(token?: string): string {
  const decodedToken = jwtDecode(token as string) as DecodedToken;

  if (decodedToken.c_user_id) {
    return decodedToken.c_user_id;
  }

  throw new Error("Could not find user ID in token");
}

/** Zensei `ApiResponse` error envelope (`http_response.go`). */
export type ZenseiApiErrorBody = {
  error?: {
    message?: string;
    /** Set where a client branches on the refusal, e.g. `connection_replace_required`. */
    code?: string;
  };
  data?: unknown;
  pagination?: unknown;
};

/**
 * Parsed JSON body we surface from the org API. Every Zensei endpoint returns
 * the same envelope, so `error?.message` is always the field to read.
 */
export type DefaultApiErrorBody = ZenseiApiErrorBody | undefined;

export type ApiErrorOptions<
  TData extends DefaultApiErrorBody = DefaultApiErrorBody,
> = {
  status: number;
  statusText: string;
  data: TData;
  cause?: unknown;
};

/**
 * Thrown by {@link handleAxiosError} for failed API calls. Default generic
 * matches {@link DefaultApiErrorBody}; pass a narrower generic only when you
 * construct `ApiError` yourself with a known body type.
 */
export class ApiError<
  TData extends DefaultApiErrorBody = DefaultApiErrorBody,
> extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly data: TData;

  constructor(message: string, options: ApiErrorOptions<TData>) {
    super(message, { cause: options.cause });
    this.name = "ApiError";
    this.status = options.status;
    this.statusText = options.statusText;
    this.data = options.data;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export function isApiError(
  error: unknown,
): error is ApiError<DefaultApiErrorBody> {
  return error instanceof ApiError;
}

export function isZenseiApiErrorBody(
  body: DefaultApiErrorBody,
): body is ZenseiApiErrorBody {
  if (body === undefined) {
    return false;
  }
  return typeof body === "object" && body !== null;
}

/** HTTP status behind a failed call; 0 for a network failure, undefined otherwise. */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (isApiError(error)) return error.status;
  if (axios.isAxiosError(error)) return error.response?.status;
  return undefined;
}

export function getApiErrorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message || "Something went wrong. Please try again.";
  }
  return "Something went wrong. Please try again.";
}

export function handleAxiosError(e: Error | unknown): never {
  if (axios.isAxiosError(e) && e.response) {
    const { status, statusText, data } = e.response;

    throw new ApiError(e.message, {
      status,
      statusText: statusText ?? "",
      data: data,
      cause: e,
    });
  }

  throw new ApiError(
    e instanceof Error ? e.message : "An unexpected error occurred",
    {
      status: 0,
      statusText: "",
      data: undefined,
      cause: e,
    },
  );
}
